import {
  Card,
  Grid2 as Grid,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import { DocumentMetrics } from '@/types/documents'
import { convertSecondsToHours } from '@/utils/time'
import { formatNumber } from '@/utils/metrics'
import GeneralInfoBox, {
  GeneralInfoBoxContent,
} from '@/components/data-visualization/GeneralInfoBox'
import PercentageColorBar from '@/components/data-visualization/PercentageColorBar'

type GeneralInfoCardsProps = {
  addedDocumentsMetrics?: DocumentMetrics
  completedDocumentsMetrics: {
    count: number | null
    avg_time_to_completion: number
    overall_stp: number
  }
}

export default function GeneralInfoCards({
  addedDocumentsMetrics,
  completedDocumentsMetrics,
}: GeneralInfoCardsProps) {
  const isNotXs = useMediaQuery((theme) => theme.breakpoints.up('sm'))

  return (
    <>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Typography>Added</Typography>
      </Grid>
      {isNotXs && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography>Completed</Typography>
        </Grid>
      )}

      {/* Added Documents Count */}
      <Grid size={{ xs: 6, sm: 2 }}>
        <Card
          elevation={2}
          sx={{
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant="h3" component="p">
            {addedDocumentsMetrics?.count || 0}
          </Typography>
        </Card>
      </Grid>

      {/* Averaged Added */}
      <Grid size={{ xs: 6, sm: 2 }}>
        <Card
          elevation={2}
          sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            rowGap: 1.5,
            height: '100%',
          }}
        >
          <GeneralInfoBoxContent
            label="Average Added per Hour"
            shrinkValueSize
            value={
              addedDocumentsMetrics
                ? formatNumber(addedDocumentsMetrics?.avg_docs_per_hour)
                : 0
            }
          />
          <GeneralInfoBoxContent
            label="Average Added per Minute"
            shrinkValueSize
            value={
              addedDocumentsMetrics
                ? formatNumber(addedDocumentsMetrics?.avg_docs_per_minute)
                : 0
            }
          />
        </Card>
      </Grid>

      {/* Pages */}
      <Grid size={{ xs: 6, sm: 2 }}>
        <Card
          elevation={2}
          sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            rowGap: 1.5,
            height: '100%',
          }}
        >
          <GeneralInfoBoxContent
            label="Pages Added"
            shrinkValueSize
            value={addedDocumentsMetrics?.page_count || 0}
          />
          <GeneralInfoBoxContent
            label="Average Pages per Doc"
            shrinkValueSize
            value={
              addedDocumentsMetrics
                ? formatNumber(addedDocumentsMetrics?.avg_page_count)
                : 0
            }
          />
        </Card>
      </Grid>
      {!isNotXs && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography>Completed</Typography>
        </Grid>
      )}
      {/* Completed Documents Count */}
      <Grid size={{ xs: 6, sm: 2 }}>
        <Card
          elevation={2}
          sx={{
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant="h3" component="p">
            {completedDocumentsMetrics.count}
          </Typography>
        </Card>
      </Grid>
      {/* Averaged Completed */}
      <Grid size={{ xs: 6, sm: 2 }}>
        <GeneralInfoBox
          label="Average Time to Completion"
          value={
            completedDocumentsMetrics.avg_time_to_completion
              ? convertSecondsToHours(
                  completedDocumentsMetrics.avg_time_to_completion,
                )
              : '---'
          }
          sx={{ pb: 1 }}
        />
      </Grid>
      {/* Overall STP */}
      <Grid size={{ xs: 6, sm: 2 }}>
        <GeneralInfoBox
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">Overall STP</Typography>
              <Tooltip title='The percentage of documents that move through the workflow to a "Done" state without stopping.'>
                <HelpOutline sx={{ color: 'text.secondary', fontSize: 18 }} />
              </Tooltip>
            </Stack>
          }
          value={`${completedDocumentsMetrics.overall_stp || 0}%`}
        >
          <PercentageColorBar
            percentage={completedDocumentsMetrics.overall_stp || 0}
          />
        </GeneralInfoBox>
      </Grid>
    </>
  )
}
